import { Box, Divider, Grid2 as Grid, Stack, Switch } from "@mui/material";
import { isEmpty, isNil } from "lodash";
import { useEffect, useState } from "react";
import {
  Control,
  UseFormClearErrors,
  UseFormGetValues,
  UseFormReset,
  UseFormSetValue,
  UseFormWatch,
} from "react-hook-form";

import { colors } from "../../../styles/colors";
import fontWeight from "../../../styles/mui/fontWeight";
import {
  Body,
  Header,
  Title,
} from "../../PriorAuthWorkFlow/styles/OrderingPhysician";
import {
  FollowingPhysicianDetails,
  ReferralPhysicianDetails,
} from "../../../models/PhysicianDetails";
import {
  NppesType,
  physicianNames,
  PhysicianTypeID,
} from "../../../constants/PhysicianDetails";
import { ReAuthTabData } from "../../../models/TabSessionStorage";
import { getFormattedPhoneNo, getValue } from "../../../utils";
import PhysicianInputForm from "../../PriorAuthWorkFlow/containers/PhysicianInputForm";
import { TypographyBox } from "../../../app/common/TextLabel";
import { updateTabStatus } from "../../../constants/TabSessionStorage";
import {
  flexAlignCentre,
  flexRow,
  justifyContentLeft,
} from "../../../styles/mui/styles/display";
import ReadOnlyField from "../../../components/FormPriorAuthReAuth/ReadOnlyField";
import { FieldSpacing } from "../../PriorAuthWorkFlow/styles/style";
import { Pad, PhysicianNameStyle } from "../styles/style";

export interface PropsFromState {
  getValues: UseFormGetValues<ReAuthTabData>;
  control: Control<any>;
  clearErrors: UseFormClearErrors<ReAuthTabData>;
  watch: UseFormWatch<ReAuthTabData>;
  reset: UseFormReset<ReAuthTabData>;
  setValue: UseFormSetValue<ReAuthTabData>;
  tabSessionStorage: ReAuthTabData;
  getPhysicianDetails: (referralId: number) => void;
  physicianDetailsState: ReferralPhysicianDetails;
}

type AllProps = PropsFromState;

const PhysicianDetails: React.FC<AllProps> = ({
  getValues,
  setValue,
  reset,
  control,
  tabSessionStorage,
  getPhysicianDetails,
  physicianDetailsState,
}: AllProps) => {
  const [followingPhysician, setFollowingPhysician] =
    useState<FollowingPhysicianDetails>();

  const [openFollowingPhysician, setOpenFollowingPhysician] = useState(false);
  const [
    openFollowingPhysicianAutocomplete,
    setOpenFollowingPhysicianAutocomplete,
  ] = useState(false);
  const [switchSameAsPrimary, setSwitchSameAsPrimary] =
    useState<boolean>(false);
  const [switchSameAsOrdering, setSwitchSameAsOrdering] =
    useState<boolean>(false);
  const [isReadOnly, setIsReadOnly] = useState<boolean>(false);
  const chooseFollowingPhysician = (option: any) => {
    setFollowingPhysician(option);
    if (!isNil(option)) {
      const {
        name,
        npiId,
        phoneNo,
        phoneExt,
        county,
        streetName1,
        streetName2,
        fax,
        city,
        state,
        zipCode,
      } = option;
      reset({
        ...getValues(),
        followingPhysicianDetail: {
          ...getValues("followingPhysicianDetail"),
          physicianName: name,
          physicianNpiId: npiId,
          streetName1: streetName1,
          streetName2: streetName2,
          city: city,
          county: county,
          zipCode: zipCode,
          fax: getFormattedPhoneNo(fax),
          physicianState: state,
          phoneExt: phoneExt,
          phoneNo: phoneNo ? getFormattedPhoneNo(phoneNo) : "",
          physicianTypeId: 3,
        },
        ...(isReadOnly && {
          primaryPhysician: {
            ...getValues("followingPhysicianDetail"),
            physicianTypeId: 1,
          },
          orderingPhysician: {
            ...getValues("followingPhysicianDetail"),
            physicianTypeId: 2,
          },
        }),
      });
    }
  };

  const followingPhysicianFormNames = {
    physicianName: "followingPhysicianDetail.physicianName",
    physicianNpiId: "followingPhysicianDetail.physicianNpiId",
    phoneNo: "followingPhysicianDetail.phoneNo",
    phoneExt: "followingPhysicianDetail.phoneExt",
    taxId: "followingPhysicianDetail.taxId",
    address: [
      "followingPhysicianDetail.streetName1",
      "followingPhysicianDetail.streetName2",
      "followingPhysicianDetail.city",
      "followingPhysicianDetail.county",
      "followingPhysicianDetail.physicianState",
      "followingPhysicianDetail.zipCode",
      "followingPhysicianDetail.latitude",
      "followingPhysicianDetail.longitude",
    ],
    fax: "followingPhysicianDetail.fax",
  };
  const handleToggle = (
    event: React.ChangeEvent<HTMLInputElement>,
    value: string,
    flag: boolean
  ) => {
    handleSwitch(event.target.checked, value, flag, true);
  };
  const handleSwitch = (
    checked: boolean,
    value: string,
    flag: boolean,
    onChange: boolean
  ) => {
    if ("same As Primary" === value) {
      !onChange && !switchSameAsPrimary && setSwitchSameAsPrimary(flag);
      switchSameAsOrdering && setSwitchSameAsOrdering(!flag);
      followingPhysicianAsPrimary(checked, physicianDetailsState, 1);
    } else if ("same as ordering" === value) {
      !onChange && !switchSameAsOrdering && setSwitchSameAsOrdering(flag);
      switchSameAsPrimary && setSwitchSameAsPrimary(!flag);
      followingPhysicianAsPrimary(checked, physicianDetailsState, 2);
    }
    onChange &&
      ("same As Primary" === value
        ? setSwitchSameAsPrimary(flag)
        : setSwitchSameAsOrdering(flag));
  };

  useEffect(() => {
    if (!switchSameAsPrimary && !switchSameAsOrdering && isEmpty(getValue(tabSessionStorage,"followingPhysicianDetail.physicianName"))) {
      clearFollowingPhysicianDetails();
    }
  }, [switchSameAsPrimary, switchSameAsOrdering]);

  const clearFollowingPhysicianDetails = () => {
    if (isReadOnly) {
      setIsReadOnly(false);
    }
    setFollowingPhysician(undefined);
    reset({
      ...getValues(),
      followingPhysicianDetail: {
        physicianName: "",
        physicianNpiId: "",
        streetName1: "",
        streetName2: "",
        city: "",
        county: "",
        physicianState: "",
        zipCode: "",
        fax: "",
        phoneExt: "",
        phoneNo: "",
        taxId: "",
        physicianTypeId: 3,
        physicianSameAsPrimary: false,
        physicianSameAsOrdering: false,
      },
    });
  };

  useEffect(() => {
    getPhysicianDetails(tabSessionStorage.referralId);
    reset({
      ...getValues(),
      reAuthTabStatus: updateTabStatus(1, getValues("reAuthTabStatus")),
    });
  }, []);

  const followingPhysicianAsPrimary = (
    checked: boolean,
    physicianDetailsResp: any,
    type: number
  ) => {
    let followingPhysicianUpdate = physicianDetailsResp.orderingPhysician;
    const physicianTypeId = PhysicianTypeID.FOLLOWING;
    followingPhysicianUpdate = {
      ...followingPhysicianUpdate,
    };

    // primary
    if (type === 1) {
      followingPhysicianUpdate = physicianDetailsResp.primaryPhysician;
    }

    followingPhysicianUpdate = {
      ...followingPhysicianUpdate,
      physicianTypeId,
    };

    if (checked) {
      clearFollowingPhysicianDetails();
      setFollowingPhysician({
        ...followingPhysicianUpdate,
      });

      reset({
        ...getValues(),
        followingPhysicianDetail: {
            ...followingPhysicianUpdate,
            physicianTypeId: physicianTypeId
        },
      });
    } else {
      setIsReadOnly(false);
      setFollowingPhysician(undefined);

      reset({
        ...getValues(),
        followingPhysicianDetail: {
          physicianName: "",
          physicianNpiId: "",
          streetName1: "",
          streetName2: "",
          city: "",
          county: "",
          physicianState: "",
          zipCode: "",
          fax: "",
          phoneExt: "",
          phoneNo: "",
          taxId: "",
          physicianTypeId: 3,
          physicianSameAsPrimary: false,
          physicianSameAsOrdering: false,
        },
      });
    }
     
    const toggleKeys = {
      physicianSameAsPrimary: false,
      physicianSameAsOrdering: false,
    };
    // primary
      if (type === 1) {
        if (checked) {
          toggleKeys.physicianSameAsPrimary = true;
          toggleKeys.physicianSameAsOrdering = false;
        } else {
          toggleKeys.physicianSameAsPrimary = false;
        }
        // ordering
      } else if (type === 2) {
        if (checked) {
          toggleKeys.physicianSameAsPrimary = false;
          toggleKeys.physicianSameAsOrdering = true;
        } else {
          toggleKeys.physicianSameAsOrdering = false;
        }
      }
      if (
        toggleKeys.physicianSameAsPrimary ||
        toggleKeys.physicianSameAsOrdering
      ) {
        setIsReadOnly(true);
      } else {
        setIsReadOnly(false);
      }
    reset({
        ...getValues(),
        followingPhysicianDetail: {
          ...getValues("followingPhysicianDetail"),
          physicianSameAsPrimary: toggleKeys.physicianSameAsPrimary,
          physicianSameAsOrdering: toggleKeys.physicianSameAsOrdering,
        },
    });
  };

  const checkSameAsPrimary = (
    followingPhysicianDetailsResp: FollowingPhysicianDetails
  ) => {
    if (
      getValue(followingPhysicianDetailsResp, "physicianSameAsPrimary") === true
    ) {
      setSwitchSameAsPrimary(true);
      setSwitchSameAsOrdering(false);
      handleSwitch(true, "same as primary", !switchSameAsPrimary, false);
    } else {
        setSwitchSameAsPrimary(false);
        setIsReadOnly(true);
    }
  };

  const checkSameAsOrdering = (
    followingPhysicianDetailsResp: FollowingPhysicianDetails
  ) => {
    if (
      getValue(followingPhysicianDetailsResp, "physicianSameAsOrdering") ===
      true
    ) {
      setSwitchSameAsOrdering(true);
      setSwitchSameAsPrimary(false);
      handleSwitch(true, "same as ordering", !switchSameAsOrdering, false);
    } else {
        setSwitchSameAsOrdering(false);
      setIsReadOnly(true);
    }
  };

  useEffect(() => {
    if (
      tabSessionStorage &&
      tabSessionStorage.followingPhysicianDetail &&
      getValue(tabSessionStorage, "followingPhysicianDetail.physicianId") > 0
    ) {
      checkSameAsPrimary(
        getValue(tabSessionStorage, "followingPhysicianDetail")
      );
      checkSameAsOrdering(
        getValue(tabSessionStorage, "followingPhysicianDetail")
      );
    }
  }, [tabSessionStorage]);
  return (
    <>
      <Grid size={{ xs: 12 }} pt={"0.5rem"} sx={{ marginX: 2 }}>
        <Grid container>
          <Grid size={{ xs: 12 }}>
            <Grid container sx={Header}>
              <Grid size={{ xs: 8 }}>
                <Box sx={Title}>
                  {TypographyBox(
                    "Primary Care Physician",
                    colors.fonts[20],
                    fontWeight[5]
                  )}
                </Box>
              </Grid>
            </Grid>
            <Grid container sx={flexRow} mt={2}>
              <Grid size={{ xs: 2 }} sx={PhysicianNameStyle}>
                <ReadOnlyField
                  label={"Physician Name:"}
                  value={getValue(physicianDetailsState, "primaryPhysician.physicianName")}
                />
              </Grid>
              <Grid size={{ xs: 2 }} sx={FieldSpacing}>
                <ReadOnlyField
                  value={getValue(physicianDetailsState, "primaryPhysician.physicianNpiId")}
                  label={"NPI:"}
                />
              </Grid>
                <Grid size={{ xs: 3 }} sx={FieldSpacing}>
                <ReadOnlyField
                  value={
                  [
                    getValue(physicianDetailsState, "primaryPhysician.streetName1") ?? "",
                    getValue(physicianDetailsState, "primaryPhysician.streetName2") ?? "",
                    getValue(physicianDetailsState, "primaryPhysician.city") ?? "",
                    getValue(physicianDetailsState, "primaryPhysician.county") ?? "",
                    getValue(physicianDetailsState, "primaryPhysician.physicianState") ?? "",
                    getValue(physicianDetailsState, "primaryPhysician.zipCode") ?? "",
                  ]
                    .filter((part) => part && part.trim())
                    .join(", ")
                  }
                  label={"Address:"}
                />
                </Grid>
              <Grid size={{ xs: 2 }} sx={FieldSpacing}>
                <ReadOnlyField
                  value={getValue(physicianDetailsState, "primaryPhysician.taxId") ?? ""}
                  label={"Tax ID:"}
                />
              </Grid>
              <Grid size={{ xs: 1.5 }} sx={FieldSpacing}>
                <ReadOnlyField
                  value={getValue(physicianDetailsState, "primaryPhysician.phoneNo") ?? ""}
                  label={"Phone number:"}
                />
              </Grid>
              <Grid size={{ xs: 1.5 }} sx={FieldSpacing}>
                <ReadOnlyField
                  value={getValue(physicianDetailsState, "primaryPhysician.fax") ?? ""}
                  label={"Fax number:"}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Divider sx={{ marginX: 2 }} />
      <Grid size={{ xs: 12 }} pt={"0.5rem"} sx={{ marginX: 2 }}>
        <Grid container>
          <Grid size={{ xs: 12 }}>
            <Grid container sx={Header}>
              <Grid size={{ xs: 6 }}>
                <Box sx={Title}>
                  {TypographyBox(
                    "Ordering Physician",
                    colors.fonts[20],
                    fontWeight[5]
                  )}
                </Box>
              </Grid>
            </Grid>
            <Grid container sx={flexRow} mt={2}>
              <Grid size={{ xs: 2 }} sx={PhysicianNameStyle}>
                <ReadOnlyField
                  label={"Physician Name:"}
                  value={getValue(physicianDetailsState, "orderingPhysician.physicianName")}
                />
              </Grid>
              <Grid size={{ xs: 2 }} sx={FieldSpacing}>
                <ReadOnlyField
                  value={getValue(physicianDetailsState, "orderingPhysician.physicianNpiId")}
                  label={"NPI:"}
                />
              </Grid>
              <Grid size={{ xs: 3 }} sx={FieldSpacing}>
                <ReadOnlyField
                  value={
                    [
                      getValue(physicianDetailsState, "orderingPhysician.streetName1"),
                      getValue(physicianDetailsState, "orderingPhysician.streetName2"),
                      getValue(physicianDetailsState, "orderingPhysician.city"),
                      getValue(physicianDetailsState, "orderingPhysician.county"),
                      getValue(physicianDetailsState, "orderingPhysician.physicianState"),
                      getValue(physicianDetailsState, "orderingPhysician.zipCode"),
                    ]
                      .filter((part) => part && part.trim())
                      .join(", ")
                  }
                  label={"Address:"}
                />
              </Grid>
              <Grid size={{ xs: 2 }} sx={FieldSpacing}>
                <ReadOnlyField
                  value={getValue(physicianDetailsState, "orderingPhysician.taxId") ?? ""}
                  label={"Tax ID:"}
                />
              </Grid>
              <Grid size={{ xs: 1.5 }} sx={FieldSpacing}>
                <ReadOnlyField
                  value={getValue(physicianDetailsState, "orderingPhysician.phoneNo") ?? ""}
                  label={"Phone number:"}
                />
              </Grid>
              <Grid size={{ xs: 1.5 }} sx={FieldSpacing}>
                <ReadOnlyField
                  value={getValue(physicianDetailsState, "orderingPhysician.fax") ?? ""}
                  label={"Fax number:"}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Divider sx={{ marginX: 2 }} />
      <Grid size={{ xs: 12 }} pt={"1rem"} sx={{ marginX: 2 }}>
        <Grid container>
          <Grid size={{ xs: 12 }}>
            <Grid container sx={Header}>
              <Grid size={{ xs: 10 }}>
                <Box sx={Title} display={"flex"} pt={2}>
                  {TypographyBox(
                    `Following Physician Details`,
                    colors.fonts[20],
                    fontWeight[5]
                  )}
                  <Box sx={[flexAlignCentre, justifyContentLeft]}>
                    {getValue(
                      physicianDetailsState.primaryPhysician,
                      "physicianName"
                    ) && (
                      <Box>
                        <Stack
                          direction="row"
                          spacing={3}
                          sx={Pad}
                        >
                          <Switch
                            value={switchSameAsPrimary}
                            inputProps={{ "aria-label": "ant design" }}
                            onChange={(e) =>
                              handleToggle(
                                e,
                                "same As Primary",
                                !switchSameAsPrimary
                              )
                            }
                            checked={switchSameAsPrimary}
                            data-testid="reauth-Same-as-primary-physician"
                          />
                          {TypographyBox(
                            "Same as primary physician",
                            colors.fonts[4],
                            fontWeight[4]
                          )}
                        </Stack>
                      </Box>
                    )}
                    <Box>
                      <Stack
                        direction="row"
                        spacing={3}
                        alignItems="center"
                        sx={Pad}
                      >
                        <Switch
                          value={switchSameAsOrdering || false}
                          inputProps={{ "aria-label": "ant design" }}
                          onChange={(e) =>
                            handleToggle(
                              e,
                              "same as ordering",
                              !switchSameAsOrdering
                            )
                          }
                          checked={switchSameAsOrdering || false}
                          data-testid="reauth-Same-as-ordering-physician"
                        />
                        {TypographyBox(
                          "Same as ordering physician",
                          colors.fonts[4],
                          fontWeight[4]
                        )}
                      </Stack>
                    </Box>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Grid>
          <Grid size={{ xs: 12 }} sx={Body} data-testid="following-physician">
            <PhysicianInputForm
              control={control}
              getValues={getValues}
              setValue={setValue}
              reset={reset}
              physician={followingPhysician || null}
              names={followingPhysicianFormNames}
              choosePhysician={chooseFollowingPhysician}
              open={openFollowingPhysician}
              setOpen={setOpenFollowingPhysician}
              openAutocomplete={openFollowingPhysicianAutocomplete}
              setOpenAutocomplete={setOpenFollowingPhysicianAutocomplete}
              physicianFormName={physicianNames.following}
              noValidate={false}
                          isReadOnly={isReadOnly}
                          setIsReadOnly={setIsReadOnly}
              renderClearButton={clearFollowingPhysicianDetails}
              type={NppesType.PhysicianOffice}
            />
          </Grid>
        </Grid>
        <Divider />
      </Grid>
    </>
  );
};

export default PhysicianDetails;
